import React, { Suspense, useState, useEffect, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import Courses from './pages/Courses';
import { UserProvider, useUser } from './context/UserContext';
import { LanguageProvider, LanguageContext } from './context/LanguageContext';
import { initializeAnalytics, trackPageView } from './analytics';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Dynamic imports with React.memo
const lazyWithRetry = (componentImport) => {
  return React.lazy(async () => {
    const pageAlreadyRefreshed = JSON.parse(
      window.localStorage.getItem('pageRefreshed') || 'false'
    );
    
    try {
      const component = await componentImport();
      window.localStorage.setItem('pageRefreshed', 'false');
      return component;
    } catch (error) {
      if (!pageAlreadyRefreshed) {
        window.localStorage.setItem('pageRefreshed', 'true');
        return window.location.reload();
      }
      throw error;
    }
  });
};

const CourseDetails = lazyWithRetry(() => import('./pages/CourseDetails'));
const CourseContent = React.memo(lazyWithRetry(() => import('./pages/CourseContent')));
const Profile = React.memo(lazyWithRetry(() => import('./pages/Profile')));
const Login = React.memo(lazyWithRetry(() => import('./pages/Login')));
const ManageCourses = React.memo(lazyWithRetry(() => import('./pages/ManageCourses')));
const AboutPage = React.memo(lazyWithRetry(() => import('./pages/AboutPage')));
const ContactPage = React.memo(lazyWithRetry(() => import('./pages/ContactPage')));
const FAQPage = React.memo(lazyWithRetry(() => import('./pages/FAQPage')));
const MyLearning = React.memo(lazyWithRetry(() => import('./pages/MyLearning')));
const ProgressTracker = React.memo(lazyWithRetry(() => import('./pages/StudyProgressPage')));
const AdminSubscribe = React.memo(lazyWithRetry(() => import('./pages/AdminSubscribe')));
const AdminDashboard = React.memo(lazyWithRetry(() => import('./pages/AdminDashboard')));
const AdminStatistics = React.memo(lazyWithRetry(() => import('./pages/AdminStatistics')));
const EditProfile = React.memo(lazyWithRetry(() => import('./pages/EditProfile')));
const ManageAllCourses = React.memo(lazyWithRetry(() => import('./pages/ManageAllCourses')));
const PrivacyPolicy = React.memo(lazyWithRetry(() => import('./pages/PrivacyPolicy')));
const TermsOfService = React.memo(lazyWithRetry(() => import('./pages/TermsOfService')));
const PaymentPage = React.memo(lazyWithRetry(() => import('./pages/PaymentPage')));
const Notifications = React.memo(lazyWithRetry(() => import('./pages/Notifications')));
const PaymentConfirmationPage = React.memo(lazyWithRetry(() => import('./pages/PaymentConfirmationPage')));
const TeacherDashboard = React.memo(lazyWithRetry(() => import('./pages/TeacherDashboard')));
const ForgotPassword = React.memo(lazyWithRetry(() => import('./pages/ForgotPassword')));


// Improved ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Enhanced Loading Component
const LoadingFallback = () => {
  const { language } = useContext(LanguageContext) || {};
  return (
    <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
      <div className="text-center">
        <div className="inline-flex items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
          <span className="ml-4 text-gray-700 text-lg">
            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
          </span>
        </div>
      </div>
    </div>
  );
};

// Enhanced Error Boundary
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const { language } = useContext(LanguageContext) || {};
  const navigate = useNavigate();

  const handleReset = () => {
    resetErrorBoundary();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {language === 'ar' ? 'حدث خطأ ما' : 'Something went wrong'}
        </h1>
        <p className="text-gray-600 mb-6">{error.message}</p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {language === 'ar' ? 'العودة للصفحة الرئيسية' : 'Go to Home'}
          </button>
          <button
            onClick={resetErrorBoundary}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {language === 'ar' ? 'حاول مرة أخرى' : 'Try again'}
          </button>
        </div>
      </div>
    </div>
  );
};

ErrorFallback.propTypes = {
  error: PropTypes.object.isRequired,
  resetErrorBoundary: PropTypes.func.isRequired
};

// Enhanced Protected Route
const ProtectedRoute = React.memo(({ children, requiredRoles = [] }) => {
  const { user, userData } = useUser();
  const location = useLocation();
  const { language } = useContext(LanguageContext) || {};

  if (user === undefined) {
    return <LoadingFallback />;
  }

  const currentRole = user?.role || userData?.role;
  const isAuthorized = !requiredRoles.length || 
                      (currentRole && requiredRoles.includes(currentRole));

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAuthorized) {
    toast.error(language === 'ar' ? 'غير مصرح بالوصول' : 'Unauthorized access');
    return <Navigate to="/" replace />;
  }

  return children;
});

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRoles: PropTypes.arrayOf(PropTypes.oneOf(['admin', 'teacher', 'student']))
};

// Route configurations
const publicRoutes = [
  { path: '/', element: <HomePage /> },
  { path: '/courses', element: <Courses /> },
  { path: '/course/:id', element: <CourseDetails /> },
  { path: '/login', element: <Login /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/faq', element: <FAQPage /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/terms', element: <TermsOfService /> },
  { path: '/privacy-policy', element: <PrivacyPolicy /> }

];

const protectedRoutes = [
  { path: '/profile', element: <Profile /> },
  { path: '/edit-profile', element: <EditProfile /> },
  { path: '/my-learning', element: <MyLearning /> },
  { path: '/study-progress', element: <ProgressTracker /> },
  { path: '/course-content/:id', element: <CourseContent /> },
  { 
    path: '/payment/:courseId', 
    element: <PaymentPage />
  },
  { path: '/notifications', element: <Notifications /> }
];

const instructorRoutes = [
  { path: '/instructor/courses', element: <ManageCourses /> },
  { path: '/teacher-dashboard', element: <TeacherDashboard /> }
];

const adminRoutes = [
  { path: '/manage-all-courses', element: <ManageAllCourses /> },
  { path: '/admin-subscriptions', element: <AdminSubscribe /> },
  { path: '/admin-dashboard', element: <AdminDashboard /> },
  { path: '/admin-statistics', element: <AdminStatistics /> },
  { path: '/payment-confirmation', element: <PaymentConfirmationPage /> }
];

// Main App Component
function App() {
  const [loading, setLoading] = useState(true);
  const [appError, setAppError] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await initializeAnalytics();
        
        if (window.matchMedia('(forced-colors: active)').matches) {
          document.documentElement.style.setProperty(
            'forced-color-adjust', 
            'none',
            'important'
          );
        }
        
        setLoading(false);
      } catch (error) {
        console.error('App initialization error:', error);
        setAppError(error);
      }
    };

    initializeApp();
  }, []);

  if (appError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Application Error</h1>
          <p className="mt-2 text-gray-700">{appError.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <UserProvider>
        <Router basename={process.env.PUBLIC_URL}>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <AppContent loading={loading} />
          </ErrorBoundary>
        </Router>
      </UserProvider>
    </LanguageProvider>
  );
}

// App Content Component
const AppContent = React.memo(({ loading }) => {
  const { user, logout } = useUser();
  const { language } = useContext(LanguageContext) || {};
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView(location.pathname);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.title = language === 'ar' ? 'منصة التعلم' : 'Learning Platform';
  }, [location.pathname, language]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/', { replace: true });
      toast.success(language === 'ar' ? 'تم تسجيل الخروج بنجاح' : 'Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(error.message);
    }
  }, [logout, navigate, language]);

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <div className="flex flex-col min-h-screen" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Navbar user={user} handleLogout={handleLogout} />
      <ScrollToTop />
      
      <main className="flex-grow p-4 md:p-6 lg:p-8 container mx-auto mt-16 md:mt-20">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {publicRoutes.map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={route.element} 
                />
              ))}

              {protectedRoutes.map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={<ProtectedRoute>{route.element}</ProtectedRoute>} 
                />
              ))}

              {instructorRoutes.map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={<ProtectedRoute requiredRoles={['teacher']}>{route.element}</ProtectedRoute>} 
                />
              ))}

              {adminRoutes.map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={<ProtectedRoute requiredRoles={['admin']}>{route.element}</ProtectedRoute>} 
                />
              ))}

              <Route path="*" element={
                <div className="text-center py-20">
                  <h1 className="text-4xl font-bold text-gray-800">404</h1>
                  <p className="text-gray-600 mt-4">
                    {language === 'ar' ? 'الصفحة غير موجودة' : 'Page not found'}
                  </p>
                  <button 
                    onClick={() => navigate('/')}
                    className="mt-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {language === 'ar' ? 'العودة للصفحة الرئيسية' : 'Go to Home'}
                  </button>
                </div>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      
      <ToastContainer
        position={language === 'ar' ? 'top-left' : 'top-right'}
        rtl={language === 'ar'}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Footer />
    </div>
  );
});

AppContent.propTypes = {
  loading: PropTypes.bool.isRequired
};

export default App;